import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listHeals, type RunHealStatus, type RunHealSummary } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatRelativeTime } from "./TracesPage";

/**
 * Self-Heal index — recent heal reports uploaded via `uaip heal …` (CLI),
 * the scheduler, or the future "re-heal" dashboard button.
 *
 * Each row drills into /heals/:id where the user triages per-element
 * accept / reject / override decisions. Status badge colour tracks the
 * lifecycle: proposed → in-review → partially-accepted → accepted →
 * pr-opened → merged (or discarded). The counts column (exact/soft/
 * missing) gives a quick signal for how gnarly a given heal is — a
 * page with many `missing` rows is one where fingerprints didn't match
 * anything, usually meaning the UI was refactored rather than just
 * tweaked.
 */
export function HealsPage(): JSX.Element {
  const { user, ready } = useAuth();
  const [heals, setHeals] = useState<RunHealSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const rows = await listHeals(100);
      setHeals(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    // Heals are scoped to the signed-in user, so skip the fetch while
    // we're either still resolving the session or known-anonymous — the
    // signed-out CTA below renders the whole view in that case.
    if (!ready || !user) return;
    load();
  }, [ready, user, load]);

  if (ready && !user) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900 p-6 text-sm text-slate-300">
        <p className="font-medium text-slate-100">Sign in to manage self-heals</p>
        <p className="mt-1 text-slate-400">
          Heal reports are user-owned — the triage queue only shows heals
          produced by your own crawls and uploads.
        </p>
        <Link
          to="/sign-in?redirect=/heals"
          className="mt-4 inline-block rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Self-Heal</h1>
          <p className="mt-1 text-sm text-slate-400">
            When a crawl drifts, <code className="rounded bg-slate-800 px-1 text-slate-200">uaip heal</code>{" "}
            fingerprints elements and proposes new selectors. Click a row to
            review and approve changes — approved heals can be sent as a
            GitHub pull request.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 hover:border-slate-600 disabled:opacity-50"
        >
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-700 bg-rose-900/40 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {heals === null && !error && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
          Loading heals…
        </div>
      )}

      {heals !== null && heals.length === 0 && !error && (
        <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center">
          <p className="text-slate-300">No heal reports yet.</p>
          <p className="mt-1 text-sm text-slate-500">
            Run{" "}
            <code className="rounded bg-slate-800 px-1 text-slate-200">
              uaip heal &lt;previous-crawl&gt; &lt;current-crawl&gt; --upload
            </code>{" "}
            to produce one. Scheduled crawls will also auto-diff against
            their previous successful run once heal-on-schedule is enabled.
          </p>
        </div>
      )}

      {heals !== null && heals.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Heal id</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2 font-medium">Elements</th>
                <th className="px-4 py-2 font-medium">Breakdown</th>
                <th className="px-4 py-2 font-medium">Soft confidence</th>
                <th className="px-4 py-2 font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-950/40">
              {heals.map((h) => (
                <tr key={h.id} className="hover:bg-slate-900/60">
                  <td className="px-4 py-2">
                    <HealStatusBadge status={h.status} />
                  </td>
                  <td className="px-4 py-2">
                    <Link
                      to={`/heals/${encodeURIComponent(h.id)}`}
                      className="font-mono text-xs text-indigo-300 hover:text-indigo-200"
                    >
                      {h.id}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-slate-300">{h.source}</td>
                  <td className="px-4 py-2 tabular-nums text-slate-300">
                    {h.totalCount}
                  </td>
                  <td className="px-4 py-2">
                    <CountsPill
                      exact={h.exactCount}
                      soft={h.softCount}
                      missing={h.missingCount}
                    />
                  </td>
                  <td className="px-4 py-2 tabular-nums text-slate-300">
                    {h.softCount === 0
                      ? "—"
                      : h.meanSoftConfidence.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-slate-400">
                    {formatRelativeTime(h.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Colour-coded badge for the 7 possible heal statuses. Shared with
 * HealReviewPage (imported via `export` below — saves duplicating the
 * class-name tree in two files).
 */
export function HealStatusBadge({ status }: { status: RunHealStatus }): JSX.Element {
  const cls = healStatusClass(status);
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {status}
    </span>
  );
}

function healStatusClass(status: RunHealStatus): string {
  switch (status) {
    case "proposed":
      return "bg-slate-800 text-slate-300 border-slate-700";
    case "in-review":
      return "bg-amber-900/40 text-amber-300 border-amber-700";
    case "partially-accepted":
      return "bg-sky-900/40 text-sky-300 border-sky-700";
    case "accepted":
      return "bg-emerald-900/40 text-emerald-300 border-emerald-700";
    case "pr-opened":
      return "bg-indigo-900/40 text-indigo-300 border-indigo-700";
    case "merged":
      return "bg-fuchsia-900/40 text-fuchsia-300 border-fuchsia-700";
    case "discarded":
      return "bg-rose-900/40 text-rose-300 border-rose-700";
    default:
      return "bg-slate-800 text-slate-300 border-slate-700";
  }
}

function CountsPill({
  exact,
  soft,
  missing,
}: {
  exact: number;
  soft: number;
  missing: number;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span
        className="rounded border border-emerald-700 bg-emerald-900/30 px-1.5 py-0.5 text-emerald-300"
        title="Exact matches"
      >
        {exact} exact
      </span>
      <span
        className="rounded border border-amber-700 bg-amber-900/30 px-1.5 py-0.5 text-amber-300"
        title="Soft (similarity) matches"
      >
        {soft} soft
      </span>
      <span
        className="rounded border border-rose-700 bg-rose-900/30 px-1.5 py-0.5 text-rose-300"
        title="Elements no longer present"
      >
        {missing} missing
      </span>
    </div>
  );
}
